"use strict";
/**
 * 「正在编辑哪一段」的跨窗口消息协议（控制台 ↔ iframe 里的预览页）。
 *
 * ## 为什么需要它
 *
 * 控制台左边是区块表单，右边是预览。一页有十几段时，客户在左边改「联系方式」，
 * 右边却不知道要往哪看 —— 只能滚一遍去找，找不到就以为没生效。
 *
 * 这两边**跨站**（`console.icetus.net` 与客户自己的域名），控制台拿不到
 * iframe 的 DOM，量不出某一段在页面上的位置。所以位置只能由**页面自己**报，
 * 控制台能做的只是"告诉它：这一段"。
 *
 * ## 协议本身的约束
 *
 * - 消息只带**候选 id**，不带选择器。跨站收消息的一方不能替对方拼 CSS 选择器
 *   （拼错就是 `SyntaxError`，拼对了是别人的 DOM），只认"这几个 id 里能找到哪个"。
 * - id 有两条线索，缺一不可：主题实例 id（`data-instance`，种子内容走这条）与
 *   区块 id（`data-block-id`，客户自己加的块走这条）。只给一条，另一类区块就高亮不出来。
 * - 回执只说 `found`，不回元素信息：控制台不需要知道它长什么样，只需要知道
 *   "这次没找到"，好当面说清"这一段被隐藏了 / 当前版式没有这一格"。
 * - 消息里带 `source` 做标记。**它不是安全边界**（任何人都能伪造），而是一个
 *   廉价的过滤：站点页面上可能同时有别的 postMessage 通信（表单嵌入、聊天插件）。
 *   真正让这件事安全的是一件事：只有预览模式下的页面才装这段脚本，而普通访客
 *   拿不到预览地址 —— 令牌是签名过的。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.HIGHLIGHT_RESULT_TYPE = exports.HIGHLIGHT_CLEAR_TYPE = exports.HIGHLIGHT_TYPE = exports.HIGHLIGHT_REPLY_SOURCE = exports.HIGHLIGHT_SOURCE = void 0;
exports.highlightIdsOf = highlightIdsOf;
exports.buildHighlightMessage = buildHighlightMessage;
exports.buildHighlightClearMessage = buildHighlightClearMessage;
exports.buildHighlightResult = buildHighlightResult;
exports.readHighlightCommand = readHighlightCommand;
exports.readHighlightResult = readHighlightResult;
/** 控制台 → 预览页。也用来在预览页脚本里判断"这条消息是给我的" */
exports.HIGHLIGHT_SOURCE = 'icetus-console';
/** 预览页 → 控制台。与上面分开，避免两边各写一份字符串写错一个字母就静默失效 */
exports.HIGHLIGHT_REPLY_SOURCE = 'icetus-preview';
exports.HIGHLIGHT_TYPE = 'icetus:block-highlight';
exports.HIGHLIGHT_CLEAR_TYPE = 'icetus:block-highlight-clear';
exports.HIGHLIGHT_RESULT_TYPE = 'icetus:block-highlight-result';
/** 候选 id 的上限。正常情况下两个（实例 id + 块 id），留点余量但别让人塞一百个 */
const MAX_IDS = 4;
/** 标签只用来画一枚小徽标，超出会被裁掉；限长是为了别让一条消息带上整段正文 */
const MAX_LABEL = 40;
/**
 * 一个区块在预览页里的候选 id。
 *
 * 顺序有意义：实例 id 先。客户自己加的块没有实例 id，用块 id 兜底 ——
 * 两类块都要能被框住，否则客户加了一段、一编辑预览里没反应，
 * 他会以为是"自己加的块不支持预览"。
 */
function highlightIdsOf(block) {
    const ids = [];
    for (const raw of [block.instanceId, block.id]) {
        const id = typeof raw === 'string' ? raw.trim() : '';
        if (!id || ids.includes(id))
            continue;
        ids.push(id);
    }
    return ids.slice(0, MAX_IDS);
}
function buildHighlightMessage(target) {
    return {
        source: exports.HIGHLIGHT_SOURCE,
        type: exports.HIGHLIGHT_TYPE,
        ids: target.ids.slice(0, MAX_IDS),
        label: target.label.slice(0, MAX_LABEL),
    };
}
function buildHighlightClearMessage() {
    return { source: exports.HIGHLIGHT_SOURCE, type: exports.HIGHLIGHT_CLEAR_TYPE };
}
function buildHighlightResult(found) {
    return { source: exports.HIGHLIGHT_REPLY_SOURCE, type: exports.HIGHLIGHT_RESULT_TYPE, found };
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
/**
 * 解析一条**发给预览页**的消息。
 *
 * 返回 `null` 表示"不是这个协议的消息"，调用方应当直接忽略 ——
 * 页面上的 message 事件很杂（浏览器插件、嵌入的客服脚本），
 * 每一条都回一句"看不懂"会把控制台的提示刷成噪音。
 */
function readHighlightCommand(data) {
    if (!isRecord(data))
        return null;
    if (data['source'] !== exports.HIGHLIGHT_SOURCE)
        return null;
    if (data['type'] === exports.HIGHLIGHT_CLEAR_TYPE)
        return { kind: 'clear' };
    if (data['type'] !== exports.HIGHLIGHT_TYPE)
        return null;
    const raw = data['ids'];
    if (!Array.isArray(raw))
        return null;
    const ids = raw
        .filter((item) => typeof item === 'string')
        .map((item) => item.trim())
        .filter((item) => item.length > 0 && item.length <= 128)
        .slice(0, MAX_IDS);
    if (ids.length === 0)
        return null;
    const label = typeof data['label'] === 'string' ? data['label'].trim().slice(0, MAX_LABEL) : '';
    return { kind: 'highlight', target: { ids, label } };
}
/** 解析一条**发给控制台**的回执。`null` = 不是回执，别动 UI */
function readHighlightResult(data) {
    if (!isRecord(data))
        return null;
    if (data['source'] !== exports.HIGHLIGHT_REPLY_SOURCE)
        return null;
    if (data['type'] !== exports.HIGHLIGHT_RESULT_TYPE)
        return null;
    return data['found'] === true;
}
//# sourceMappingURL=preview-highlight.js.map